import { faLink, faTrafficLight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo } from 'react';
import { DomainGq } from '../../../../../graphql/projects/models/DomainGq';
import { DomainRankFilterInputGq } from '../../../../../graphql/projects/models/DomainRankFilterInputGq';
import { KeywordSortByTypeGq } from '../../../../../graphql/projects/models/KeywordSortByTypeGq';
import VoxelMenuDivider from '../../../../voxel/PopoverMenu/VoxelMenuDivider';
import VoxelPopoverMenuItem from '../../../../voxel/PopoverMenu/VoxelPopoverMenuItem';
import HeadCellNumber from '../../Shared/HeadCellNumber';
import HeadCellText from '../../Shared/HeadCellText';
import { KeywordTableColumn, KeywordTableColumnTypes } from '../KeywordTableColumnPicker/models';
import { KeywordTableState } from '../logic/useKeywordTableState';

export interface HeadCellDomainProps {
  keywordTableState: KeywordTableState;
  domain: DomainGq;
  showUrl?: boolean;
  showTraffic?: boolean;
  isProgressPage?: boolean;
  tableContainerRef?: HTMLDivElement;
}

const HeadCellDomain: React.FC<HeadCellDomainProps> = ({
  keywordTableState,
  domain,
  showUrl,
  showTraffic,
  isProgressPage,
  tableContainerRef,
}) => {
  const { setSortingState, sortingState, filterState, setFilterState } = keywordTableState;

  const handleChangeColumn = (state: Partial<KeywordTableColumn>) =>
    keywordTableState.columnPickerBag.setColumnState((prev) =>
      prev.map((p) => {
        if (p.type === KeywordTableColumnTypes.Rank && p.id == domain.id) {
          return { ...p, ...state };
        }
        return p;
      })
    );

  const domainRankFilter = useMemo(
    () => filterState.domainRankFilters.find((dr) => dr.domainId === domain.id),
    [filterState.domainRankFilters, domain.id]
  );

  const handleDomainFilterChange = useCallback(
    (change: Partial<DomainRankFilterInputGq>) => {
      setFilterState((prev) => {
        const domainRankFilters = prev.domainRankFilters.filter((dr) => dr.domainId !== domain.id);
        const previousValue = prev.domainRankFilters.find((dr) => dr.domainId === domain.id);
        domainRankFilters.push({
          ...previousValue,
          domainId: domain.id,
          ...change,
        });
        return {
          ...prev,
          domainRankFilters,
        };
      });
    },
    [domain.id]
  );

  return (
    <>
      <HeadCellNumber
        basicConfig={{
          title: `${domain.name}`,
          tooltip: 'Domain position',
          showSorting: true,
          className: `
            ${isProgressPage && 'border-t border-l'}
            ${!isProgressPage && (showTraffic || showUrl) && 'border-t border-l'}
          `,
        }}
        basicState={{
          tableContainerRef,
          showColumn: true,
          hideColumn: () => handleChangeColumn({ order: undefined }),
          sortDirectionState:
            sortingState?.sortByType === KeywordSortByTypeGq.DOMAIN_RANK &&
            sortingState?.sortById === domain?.id
              ? sortingState?.sortDirection
              : undefined,
          toggleSortDirection: (state) =>
            keywordTableState.handleSort(
              KeywordSortByTypeGq.DOMAIN_RANK,
              domain.id,
              state,
              isProgressPage
            ),
          setColumnWidth: (width: number) =>
            keywordTableState.columnPickerBag.setColumnState((prev) =>
              prev.map((p) => {
                if (p.type === KeywordTableColumnTypes.Rank && p.id == domain.id) {
                  return { ...p, sizePx: width };
                }
                return p;
              })
            ),
        }}
        minFilterState={domainRankFilter?.minRank}
        maxFilterState={domainRankFilter?.maxRank}
        setMinFilterState={(value) => handleDomainFilterChange({ minRank: value })}
        setMaxFilterState={(value) => handleDomainFilterChange({ maxRank: value })}
      >
        <VoxelPopoverMenuItem
          icon={<FontAwesomeIcon icon={faTrafficLight} className="ml-[3px]" />}
          title="Show Traffic"
          onClick={() => handleChangeColumn({ traffic: !showTraffic })}
          selected={showTraffic}
          closePopoverOnClick={false}
        />

        <VoxelPopoverMenuItem
          closePopoverOnClick={false}
          icon={<FontAwesomeIcon icon={faLink} />}
          title="Show Ranking URL"
          onClick={() => handleChangeColumn({ url: !showUrl })}
          selected={showUrl}
        />
        <VoxelMenuDivider />
      </HeadCellNumber>

      {isProgressPage && (
        <HeadCellNumber
          basicConfig={{
            title: `Change`,
            tooltip: 'Rank change throughout tracking progress period',
            showSorting: true,
            hideColumnDisabled: true,
            className: `
              border-t
              ${!showUrl && !showTraffic && 'border-r'}
          `,
          }}
          basicState={{
            showColumn: true,
            hideColumn: () => handleChangeColumn({}),
            sortDirectionState:
              sortingState?.sortByType === KeywordSortByTypeGq.PROGRESS_DOMAIN_RANK_CHANGE &&
              sortingState?.sortById === domain?.id
                ? sortingState?.sortDirection
                : undefined,
            toggleSortDirection: (state) =>
              keywordTableState.handleSort(
                KeywordSortByTypeGq.PROGRESS_DOMAIN_RANK_CHANGE,
                domain.id,
                state,
                isProgressPage
              ),
          }}
          allowNegative
          minFilterState={domainRankFilter?.progressMinRankChange}
          maxFilterState={domainRankFilter?.progressMaxRankChange}
          setMinFilterState={(value) => handleDomainFilterChange({ progressMinRankChange: value })}
          setMaxFilterState={(value) => handleDomainFilterChange({ progressMaxRankChange: value })}
        />
      )}

      {showTraffic && (
        <HeadCellNumber
          basicConfig={{
            title: `Traffic`,
            tooltip: 'Estimated monthly traffic based on volume and statistical CTR',
            showSorting: true,
            className: `
              whitespace-nowrap
              border-t
              ${!showUrl && !isProgressPage && 'border-r'}
        `,
          }}
          basicState={{
            showColumn: true,
            hideColumn: () => handleChangeColumn({ traffic: false }),
            sortDirectionState:
              sortingState?.sortByType === KeywordSortByTypeGq.DOMAIN_TRAFFIC &&
              sortingState?.sortById === domain?.id
                ? sortingState?.sortDirection
                : undefined,
            toggleSortDirection: (state) =>
              keywordTableState.handleSort(
                KeywordSortByTypeGq.DOMAIN_TRAFFIC,
                domain.id,
                state,
                isProgressPage
              ),
          }}
          minFilterState={domainRankFilter?.minTraffic}
          maxFilterState={domainRankFilter?.maxTraffic}
          setMinFilterState={(value) => handleDomainFilterChange({ minTraffic: value })}
          setMaxFilterState={(value) => handleDomainFilterChange({ maxTraffic: value })}
        />
      )}

      {showTraffic && isProgressPage && (
        <HeadCellNumber
          basicConfig={{
            title: `Change`,
            tooltip: 'Estimated monthly traffic change throughout tracking progress period',
            showSorting: true,
            className: `
          whitespace-nowrap
          border-t
          ${!showUrl && 'border-r'}
      `,
          }}
          basicState={{
            showColumn: true,
            hideColumn: () => handleChangeColumn({ traffic: false }),
            sortDirectionState:
              sortingState?.sortByType === KeywordSortByTypeGq.PROGRESS_DOMAIN_TRAFFIC_CHANGE &&
              sortingState?.sortById === domain?.id
                ? sortingState?.sortDirection
                : undefined,
            toggleSortDirection: (state) =>
              keywordTableState.handleSort(
                KeywordSortByTypeGq.PROGRESS_DOMAIN_TRAFFIC_CHANGE,
                domain.id,
                state,
                isProgressPage
              ),
          }}
          allowNegative
          minFilterState={domainRankFilter?.progressMinTrafficChange}
          maxFilterState={domainRankFilter?.progressMaxTrafficChange}
          setMinFilterState={(value) =>
            handleDomainFilterChange({ progressMinTrafficChange: value })
          }
          setMaxFilterState={(value) =>
            handleDomainFilterChange({ progressMaxTrafficChange: value })
          }
        />
      )}

      <HeadCellText
        skipInfoIcon
        basicConfig={{
          title: `Ranking URL`,
          tooltip: 'URL on the SERP for the corresponding rank',
          showSorting: false,
          className: `border-t border-r`,
        }}
        basicState={{
          showColumn: showUrl,
          hideColumn: () => handleChangeColumn({ url: false }),
        }}
        filterState={domainRankFilter?.rankUrlSearchTerm ?? ''}
        setFilterState={(value) => handleDomainFilterChange({ rankUrlSearchTerm: value })}
      />
    </>
  );
};

export default HeadCellDomain;
